// "Onde trabalho" (Texto 3 D4): para quem recebe de instituições (escola, universidade, hospital, clínica...),
// e não (ou não só) de alunos ou pacientes. Cada instituição tem o tipo de pagamento:
//  - Salário fixo: gera o "a receber" de todo mês;
//  - Por hora-aula: soma as aulas dadas nas turmas ligadas à instituição (chamada ou "Aula dada" do plano de aula);
//  - Por plantão / por atendimento: soma os plantões e atendimentos marcados como feitos.
// Os valores a receber ficam em "Recebimentos" (dentro da instituição), no padrão de dinheiro das fichas
// (valor previsto, recebido, data, forma e a diferença + ou −). Na aba Finanças eles aparecem nas contas do mês
// e, quando recebidos, como entrada (calculada daqui, sem lançamento guardado em dobro; ver store/financas).
// Liga e desliga em Ajustes › Abas › [aba] › Partes desta aba.
import { CartaoApoio } from '../components/Recolhivel';
import { useEffect } from 'react';
import { Pressable, Text, View } from 'react-native';
import type { ModId, Reg } from '../store/types';
import type { Colecao, Ctx, TopoProps } from './tipos';
import { ATENDIMENTO, HORA_AULA, PLANTAO, SALARIO, diferencaRecebida, nomeDoMes, tituloRecebimento, valorLiquido } from './ondeTrabalhoBase';
import { dataBr, din, junta, num, op, temNum } from './util';
import { mudarCampos, novoReg, regsDe, salvarVarios } from '../store/modulos';
import { somaMes } from '../store/financas';
import { acaoRelatorioPdf, dadosPdf, porDataPdf, secaoPdf } from '../lib/pdfFicha';
import type { BlocoPdf } from '../lib/pdf';
import { C, alpha } from '../components/theme';
import { today, horaTela } from '../lib/date';
import { N_, t } from '../lib/i18n';

// Parte B2: formas de pagamento
const FORMAS = [N_('PIX'), N_('Dinheiro'), N_('Cartão de crédito'), N_('Cartão de débito'), N_('Boleto'), N_('Transferência')];

const p2 = (n: number) => String(n).padStart(2, '0');
/** Dia do pagamento no mês (31 vira o último dia). */
const diaNoMes = (ym: string, dia: number) => { const [a, m] = ym.split('-').map(Number); return `${ym}-${p2(Math.min(Math.max(1, dia), new Date(a, m, 0).getDate()))}`; };

/** Quanto a instituição paga por unidade (o salário, a hora-aula, o plantão ou o atendimento). */
export function valorUnitario(inst: Reg) {
  const p = inst.c.pagamento;
  return num(p === SALARIO ? inst.c.salario : p === HORA_AULA ? inst.c.valorHora : p === PLANTAO ? inst.c.valorPlantao : inst.c.valorAtend);
}

/** Aulas dadas no mês nas turmas ligadas à instituição: datas com chamada ou com plano de aula "Aula dada"
 *  (a mesma aula marcada nos dois conta uma vez), vezes as horas-aula de cada aula da turma. */
export function aulasDoMes(regs: Reg[], instId: string, ym: string) {
  const turmas = regs.filter(r => r.col === 'turma' && r.c.instituicao === instId);
  let total = 0;
  const porTurma: { turma: Reg; aulas: number; horas: number }[] = [];
  for (const tr of turmas) {
    const datas = new Set(regs.filter(r => r.pai === tr.id && ((r.col === 'chamada' && r.c.data) || (r.col === 'aula' && r.c.dada && r.c.data)))
      .map(r => String(r.c.data)).filter(d => d.startsWith(ym)));
    const horas = datas.size * (num(tr.c.hAula) || 1);
    if (datas.size) porTurma.push({ turma: tr, aulas: datas.size, horas });
    total += horas;
  }
  return { total, porTurma };
}

/** Quantidade e valor do mês pelo tipo de pagamento. */
export function calculoDoMes(regs: Reg[], inst: Reg, ym: string) {
  const p = inst.c.pagamento, un = valorUnitario(inst);
  if (p === SALARIO) return { qtd: 1, valor: un };
  if (p === HORA_AULA) { const q = aulasDoMes(regs, inst.id, ym).total; return { qtd: q, valor: Math.round(q * un * 100) / 100 }; }
  const feitos = regs.filter(r => r.col === 'servico' && r.pai === inst.id && r.c.feito && String(r.c.data ?? '').startsWith(ym));
  const qtd = feitos.reduce((s, r) => s + (num(r.c.qtd) || 1), 0);
  const valor = feitos.reduce((s, r) => s + (num(r.c.qtd) || 1) * (temNum(r.c.valor) ? num(r.c.valor) : un), 0);
  return { qtd, valor: Math.round(valor * 100) / 100 };
}

/** Cria (ou atualiza, enquanto não foi recebido nem editado à mão) o "a receber" de cada mês trabalhado,
 *  do início do contrato (até 12 meses para trás) até o mês atual, sem passar do fim do contrato. */
export function gerarRecebimentos(mod: ModId, instId: string) {
  const regs = regsDe(mod);
  const inst = regs.find(r => r.id === instId);
  if (!inst || !inst.c.pagamento) return 0;
  const atual = today().slice(0, 7);
  const ini = String(inst.c.inicio || inst.criado.slice(0, 10)).slice(0, 7);
  const fim = inst.c.fim ? String(inst.c.fim).slice(0, 7) : null;
  const doInst = regs.filter(r => r.col === 'recebimento' && r.pai === inst.id);
  const novos: Reg[] = [];
  let n = 0;
  // meses já gerados alguma vez: se a pessoa excluir o "a receber" de um mês, ele não volta sozinho
  const gerados = new Set<string>(Array.isArray(inst.c.gerados) ? (inst.c.gerados as string[]) : []);
  const antes = gerados.size;
  doInst.forEach(r => { if (r.c.mes) gerados.add(String(r.c.mes)); });
  for (let ym = ini < somaMes(atual, -11) ? somaMes(atual, -11) : ini; ym <= atual && (!fim || ym <= fim); ym = somaMes(ym, 1)) {
    const x = calculoDoMes(regs, inst, ym);
    const ja = doInst.find(r => r.c.mes === ym);
    const venc = diaNoMes(inst.c.mesSeguinte ? somaMes(ym, 1) : ym, num(inst.c.diaPag) || 5);
    if (!ja) {
      if (!(x.valor > 0) || gerados.has(ym)) continue;
      gerados.add(ym);
      novos.push(novoReg('recebimento', inst.id, { mes: ym, venc, valor: x.valor, qtd: inst.c.pagamento === SALARIO ? '' : x.qtd, recebido: false }));
    } else if (!ja.c.recebido && !ja.c.manual && (num(ja.c.valor) !== x.valor || (inst.c.pagamento !== SALARIO && num(ja.c.qtd) !== x.qtd))) {
      mudarCampos(mod, ja.id, { valor: x.valor, qtd: inst.c.pagamento === SALARIO ? '' : x.qtd }); n++;
    }
  }
  salvarVarios(mod, novos);
  if (gerados.size !== antes) mudarCampos(mod, inst.id, { gerados: [...gerados].sort() });
  return n + novos.length;
}

function textoDiferenca(dif: number, moeda: string) {
  if (!dif) return '';
  return dif < 0 ? t('Diferença: {v} (taxas/descontos)', { v: '−' + din(-dif, moeda) }) : t('Diferença: {v} (juros/multa)', { v: '+' + din(dif, moeda) });
}
/** Números para o resumo e o relatório (Parte B4). */
function numeros(l: Reg[]) {
  const hoje = today(), mes = hoje.slice(0, 7);
  const previsto = l.reduce((s, r) => s + num(r.c.valor), 0);
  const rec = l.filter(r => r.c.recebido);
  const recebido = rec.reduce((s, r) => s + valorLiquido(r), 0);
  const difs = rec.reduce((s, r) => s + diferencaRecebida(r), 0);
  const aberto = l.filter(r => !r.c.recebido);
  const aReceber = aberto.reduce((s, r) => s + num(r.c.valor), 0);
  const atrasado = aberto.filter(r => r.c.venc && r.c.venc < hoje).reduce((s, r) => s + num(r.c.valor), 0);
  const doMes = l.filter(r => String(r.c.venc ?? '').startsWith(mes)).reduce((s, r) => s + valorLiquido(r), 0);
  return { previsto, recebido, difs: Math.round(difs * 100) / 100, aReceber, atrasado, doMes };
}

/** Painel no topo da instituição: o cálculo do mês e os números (e mantém o "a receber" dos meses em dia). */
function PainelInstituicao({ r, ctx, pode }: TopoProps) {
  const ym = today().slice(0, 7);
  useEffect(() => { if (pode) gerarRecebimentos(ctx.mod, r.id); }, [ctx.regs, r, pode]); // eslint-disable-line react-hooks/exhaustive-deps
  const $ = (v: number) => din(v, ctx.moeda);
  const x = calculoDoMes(ctx.regs, r, ym);
  const n = numeros(ctx.filhos(r.id, 'recebimento'));
  const p = r.c.pagamento;
  const turmas = p === HORA_AULA ? aulasDoMes(ctx.regs, r.id, ym).porTurma : [];
  const unidade = p === HORA_AULA ? t('{n} hora(s)-aula', { n: x.qtd }) : p === PLANTAO ? t('{n} plantão(ões)', { n: x.qtd }) : p === ATENDIMENTO ? t('{n} atendimento(s)', { n: x.qtd }) : '';
  return (
    <CartaoApoio titulo={'💰 ' + nomeDoMes(ym)} chave={'trabalho:mes'} resumo={p ? t('Previsão do mês: {v}', { v: $(x.valor) }) : ''} gap={4}>
      {!p ? <Text style={{ color: C.sub, fontSize: 13 }}>{t('Escolha o tipo de pagamento (Editar) para o app calcular o que você tem a receber.')}</Text> : (
        <Text style={{ color: C.text }}>{junta(unidade, t('Previsão do mês: {v}', { v: $(x.valor) }))}</Text>
      )}
      {turmas.map(x2 => <Text key={x2.turma.id} style={{ color: C.sub, fontSize: 12 }}>• {junta(x2.turma.c.nome, x2.turma.c.disciplina)}: {t('{n} aula(s) dada(s)', { n: x2.aulas })}{x2.horas !== x2.aulas ? ` (${t('{n} hora(s)-aula', { n: x2.horas })})` : ''}</Text>)}
      {p === HORA_AULA && !ctx.regs.some(tr => tr.col === 'turma' && tr.c.instituicao === r.id) && (
        <Text style={{ color: C.warn, fontSize: 12 }}>{t('Ligue as turmas a esta instituição (na turma, Editar › Instituição) para o app somar as aulas dadas.')}</Text>)}
      {p === HORA_AULA && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Conta cada dia com chamada feita ou com plano de aula marcado como "Aula dada".')}</Text>}
      {(p === PLANTAO || p === ATENDIMENTO) && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Marque cada plantão ou atendimento como feito: o app soma e calcula o valor do mês.')}</Text>}
      <View style={{ height: 4 }} />
      <Text style={{ color: C.sub, fontSize: 12 }}>{junta(t('Previsto: {v}', { v: $(n.previsto) }), t('Recebido: {v}', { v: $(n.recebido) }))}</Text>
      {!!n.difs && <Text style={{ color: n.difs < 0 ? C.danger : C.ok, fontSize: 12 }}>{textoDiferenca(n.difs, ctx.moeda)}</Text>}
      {n.aReceber > 0 && <Text style={{ color: C.warn, fontSize: 12, fontWeight: '700' }}>{t('A receber: {v}', { v: $(n.aReceber) })}{n.atrasado > 0 ? <Text style={{ color: C.danger }}> · {t('Atrasado: {v}', { v: $(n.atrasado) })}</Text> : null}</Text>}
      {pode && !!p && (
        <Pressable onPress={() => gerarRecebimentos(ctx.mod, r.id)} hitSlop={6} style={{ paddingTop: 4 }}>
          <Text style={{ color: ctx.cor, fontWeight: '700', fontSize: 12 }}>🔄 {t('Atualizar o a receber')}</Text>
        </Pressable>
      )}
    </CartaoApoio>
  );
}

/** Relatório da instituição em PDF (Parte B4): previsto, diferenças, líquido, a receber e atrasado. */
function relatorioInstituicao(ctx: Ctx, inst: Reg) {
  const $ = (v: number) => din(v, ctx.moeda);
  const l = porDataPdf(ctx.filhos(inst.id, 'recebimento'), 'venc');
  const n = numeros(l);
  const hoje = today();
  const b: BlocoPdf[] = [
    { t: 'titulo', texto: inst.c.nome || t('Instituição') },
    ...dadosPdf([[t('Tipo'), op(inst.c.tipoInst)], [t('Tipo de pagamento'), op(inst.c.pagamento)], [t('Valor'), valorUnitario(inst) ? $(valorUnitario(inst)) : ''],
      [t('Dia do pagamento'), inst.c.diaPag], [t('Início'), dataBr(inst.c.inicio)], [t('Até'), dataBr(inst.c.fim)]]),
    ...secaoPdf(t('Recebimentos'), [t('Referente a'), t('Vencimento'), t('Previsto'), t('Recebido'), t('Diferença')],
      l.map(r => { const d = diferencaRecebida(r);
        return [tituloRecebimento(r, inst), dataBr(r.c.venc), $(num(r.c.valor)),
          r.c.recebido ? junta($(valorLiquido(r)), dataBr(r.c.recebidoEm), op(r.c.forma)) : r.c.venc && r.c.venc < hoje ? t('Atrasado') : t('A receber'),
          d ? (d < 0 ? '−' : '+') + $(Math.abs(d)) : '']; }), [0.3, 0.14, 0.16, 0.26, 0.14]),
    { t: 'par', tam: 10, texto: [t('Previsto: {v}', { v: $(n.previsto) }), textoDiferenca(n.difs, ctx.moeda), t('Recebido: {v}', { v: $(n.recebido) }),
      t('A receber: {v}', { v: $(n.aReceber) }), t('Atrasado: {v}', { v: $(n.atrasado) }), t('Previsão do mês: {v}', { v: $(n.doMes) })].filter(Boolean).join('\n') },
  ];
  return { nome: junta(t('Onde trabalho'), inst.c.nome), blocos: b };
}

/** As coleções da parte "Onde trabalho" de uma aba: a instituição, os recebimentos e (se houver plantão ou
 *  atendimento) os plantões e atendimentos feitos. */
export function colecoesOndeTrabalho(o: { tipos: string[]; pagamentos: string[]; ph: string; vazio: string }): Colecao[] {
  const comServico = o.pagamentos.includes(PLANTAO) || o.pagamentos.includes(ATENDIMENTO);
  const instituicao: Colecao = {
    id: 'instituicao', nome: N_('Onde trabalho'), um: N_('Instituição'), icone: '🏢', ordem: 'titulo',
    campos: [
      { k: 'nome', r: N_('Nome da instituição'), t: 'texto', ph: o.ph },
      { k: 'tipoInst', r: N_('Tipo'), t: 'opcao', op: o.tipos },
      { k: 'pagamento', r: N_('Tipo de pagamento'), t: 'opcao', op: o.pagamentos },
      { k: 'salario', r: N_('Salário (valor do mês)'), t: 'dinheiro', mostrar: c => c.pagamento === SALARIO },
      { k: 'valorHora', r: N_('Valor da hora-aula'), t: 'dinheiro', mostrar: c => c.pagamento === HORA_AULA },
      { k: 'valorPlantao', r: N_('Valor do plantão'), t: 'dinheiro', mostrar: c => c.pagamento === PLANTAO },
      { k: 'valorAtend', r: N_('Valor por atendimento ou procedimento'), t: 'dinheiro', mostrar: c => c.pagamento === ATENDIMENTO },
      { k: 'diaPag', r: N_('Dia do pagamento'), t: 'numero' },
      { k: 'mesSeguinte', r: N_('Recebe no mês seguinte ao trabalhado'), t: 'sim' },
      { k: 'inicio', r: N_('Início'), t: 'data' },
      { k: 'fim', r: N_('Até (fim do contrato, se houver)'), t: 'data', alerta: true },
      { k: 'endereco', r: N_('Endereço'), t: 'endereco' },
      { k: 'telefone', r: N_('Telefone (RH ou financeiro)'), t: 'telefone' },
      { k: 'notas', r: N_('Anotações'), t: 'longo' },
    ],
    titulo: r => r.c.nome || '',
    sub: (r, ctx) => {
      const ab = ctx.filhos(r.id, 'recebimento').filter(x => !x.c.recebido).reduce((s, x) => s + num(x.c.valor), 0);
      return junta(op(r.c.tipoInst), op(r.c.pagamento), valorUnitario(r) > 0 && din(valorUnitario(r), ctx.moeda), ab > 0 && t('A receber: {v}', { v: din(ab, ctx.moeda) }));
    },
    novo: () => ({ pagamento: o.pagamentos[0], diaPag: 5, mesSeguinte: true, inicio: today() }),
    aoSalvar: (r, ctx) => { gerarRecebimentos(ctx.mod, r.id); },
    acoes: (r, ctx) => [
      { label: '🔄 ' + t('Atualizar o a receber'), onPress: () => { gerarRecebimentos(ctx.mod, r.id); } },
      acaoRelatorioPdf(() => relatorioInstituicao(ctx, r)),
    ],
    painelTopo: PainelInstituicao,
    vazio: o.vazio,
  };
  const recebimento: Colecao = {
    id: 'recebimento', pai: 'instituicao', nome: N_('Recebimentos'), um: N_('Recebimento'), check: 'recebido', data: 'venc', ordem: 'data-desc',
    pagamento: { tipo: 'receber', venc: 'venc', data: 'recebidoEm', valorPago: 'valorRecebido', forma: 'forma', umaMoeda: true },
    icone: r => (r.c.recebido ? '✅' : r.c.venc && r.c.venc < today() ? '⚠️' : '💰'),
    campos: [
      { k: 'ref', r: N_('Referente a'), t: 'texto', ph: N_('Ex.: Salário de outubro, 13º, férias') },
      { k: 'venc', r: N_('Data prevista do pagamento'), t: 'data', aviso: [0] },
      { k: 'qtd', r: N_('Quantidade (aulas, plantões ou atendimentos)'), t: 'numero', mostrar: c => temNum(c.qtd) },
      { k: 'valor', r: N_('Valor previsto'), t: 'dinheiro' },
      { k: 'recebido', r: N_('Recebido'), t: 'sim', seg: 'fin' },
      { k: 'recebidoEm', r: N_('Data do recebimento'), t: 'data', mostrar: c => !!c.recebido, seg: 'fin' },
      { k: 'forma', r: N_('Forma de pagamento'), t: 'opcao', op: FORMAS, mostrar: c => !!c.recebido, seg: 'fin' },
      { k: 'valorRecebido', r: N_('Valor recebido'), t: 'dinheiro', mostrar: c => !!c.recebido },
      { k: 'notas', r: N_('Anotações'), t: 'longo' },
    ],
    titulo: (r, ctx) => tituloRecebimento(r, ctx.porId(r.pai)),
    sub: (r, ctx) => { const inst = ctx.porId(r.pai), p = inst?.c.pagamento;
      const q = temNum(r.c.qtd) && num(r.c.qtd) > 0 ? (p === HORA_AULA ? t('{n} hora(s)-aula', { n: num(r.c.qtd) }) : p === PLANTAO ? t('{n} plantão(ões)', { n: num(r.c.qtd) }) : t('{n} atendimento(s)', { n: num(r.c.qtd) })) : '';
      return junta(q, din(r.c.valor, ctx.moeda), r.c.recebido ? t('recebido em {d}', { d: dataBr(r.c.recebidoEm) }) : dataBr(r.c.venc), textoDiferenca(diferencaRecebida(r), ctx.moeda)); },
    feito: r => !!r.c.recebido,
    novo: (_ctx, pai) => ({ venc: today(), valor: pai ? valorUnitario(pai) || '' : '', recebido: false, manual: true }),
    ajustar: (c, k) => {
      // Parte B2: ao marcar, já vem a data de hoje e o valor previsto (dá para mudar; a diferença aparece sozinha)
      if (k === 'recebido') return c.recebido ? { ...c, recebidoEm: c.recebidoEm || today(), valorRecebido: temNum(c.valorRecebido) ? c.valorRecebido : c.valor, forma: c.forma || 'PIX' }
        : { ...c, recebidoEm: '', valorRecebido: '' };
      if (k === 'valor' || k === 'qtd') return { ...c, manual: true };   // mudou à mão: o cálculo automático não mexe mais
      return c;
    },
    agenda: (r, ctx) => ({ start: r.c.venc, title: t('Receber: {t}', { t: junta(ctx.porId(r.pai)?.c.nome, tituloRecebimento(r, ctx.porId(r.pai))) }), notes: din(r.c.valor, ctx.moeda) }),
    resumo: (l, ctx) => { if (!l.length) return null; const n = numeros(l), $ = (v: number) => din(v, ctx.moeda);
      return junta(t('Recebido: {v}', { v: $(n.recebido) }), n.aReceber > 0 && t('A receber: {v}', { v: $(n.aReceber) }), n.atrasado > 0 && t('Atrasado: {v}', { v: $(n.atrasado) }), textoDiferenca(n.difs, ctx.moeda)); },
    filtros: [[N_('Em aberto'), r => !r.c.recebido], [N_('Atrasados'), r => !r.c.recebido && !!r.c.venc && r.c.venc < today()], [N_('Recebidos'), r => !!r.c.recebido]],
    vazio: N_('O que a instituição tem a pagar a você, mês a mês. Marque como recebido quando o dinheiro entrar: vai junto para a aba Finanças.'),
  };
  if (!comServico) return [instituicao, recebimento];
  const servico: Colecao = {
    id: 'servico', pai: 'instituicao', nome: N_('Plantões e atendimentos'), um: N_('Plantão ou atendimento'), check: 'feito', data: 'data', ordem: 'data-desc',
    icone: r => (r.c.feito ? '✅' : r.c.tipo === 'Plantão' ? '🏥' : '🩺'),
    campos: [
      { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: [N_('Plantão'), N_('Atendimento'), N_('Procedimento')] },
      { k: 'data', r: N_('Data'), t: 'data' },
      { k: 'hora', r: N_('Horário'), t: 'hora' },
      { k: 'qtd', r: N_('Quantidade'), t: 'numero' },
      { k: 'valor', r: N_('Valor de cada um (se for diferente)'), t: 'dinheiro' },
      { k: 'feito', r: N_('Feito'), t: 'sim' },
      { k: 'notas', r: N_('Anotações'), t: 'longo' },
    ],
    titulo: r => junta(op(r.c.tipo), dataBr(r.c.data)),
    sub: (r, ctx) => { const un = temNum(r.c.valor) ? num(r.c.valor) : valorUnitario(ctx.porId(r.pai) ?? r), q = num(r.c.qtd) || 1;
      return junta(horaTela(r.c.hora), q > 1 && `${q}×`, un > 0 && din(un * q, ctx.moeda), r.c.feito ? '✓ ' + t('feito') : ''); },
    feito: r => !!r.c.feito,
    novo: (_ctx, pai) => ({ tipo: pai?.c.pagamento === PLANTAO ? 'Plantão' : 'Atendimento', data: today(), qtd: 1, feito: true }),
    agenda: (r, ctx) => ({ start: r.c.data, time: r.c.hora || null, title: junta(op(r.c.tipo), ctx.porId(r.pai)?.c.nome) }),
    filtros: [[N_('Feitos'), r => !!r.c.feito], [N_('Agendados'), r => !r.c.feito]],
    vazio: N_('Marque cada plantão ou atendimento feito nesta instituição. O app soma e calcula o valor a receber do mês.'),
  };
  return [instituicao, recebimento, servico];
}

